<template>
  <div class="ds-alert" :style="cssVars">
    <div class="ds-alert__inner-wrapper">
      <component
        :is="iconInfo.iconComponent"
        :width="24"
        :height="24"
        :icon="iconInfo.icon"
        size="24px"
        class="ds-alert__icon"
      />
      <span v-if="props.message">{{ props.message }}</span>
      <span v-else>
        <slot />
      </span>
    </div>
  </div>
</template>

<script setup lang="ts">
import { defineProps, withDefaults, computed } from 'vue';
import { Colors } from '@/api';

interface Props {
  type?: 'success' | 'error' | 'warning' | 'info';
  message?: string;
}

const props = withDefaults(defineProps<Props>(), {
  type: 'info',
});

const iconInfo = computed(() => {
  switch (props.type) {
    case 'success':
      return { icon: 'check-circle', iconComponent: 'octicon' };
    case 'error':
      return { icon: 'x-circle', iconComponent: 'octicon' };
    case 'warning':
      return { icon: 'warning', iconComponent: 'fluent-icon' };
    default:
      return { icon: 'info', iconComponent: 'octicon' };
  }
});

const cssVars = computed(() => {
  let primaryColor: Colors;
  switch (props.type) {
    case 'success':
      primaryColor = Colors.GREEN;
      break;
    case 'error':
      primaryColor = Colors.RED;
      break;
    case 'warning':
      primaryColor = Colors.YELLOW;
      break;
    case 'info':
      primaryColor = Colors.BLUE;
      break;
  }
  // Use CSS variables directly - no need for expensive useThemedColors()
  return {
    '--primary-color': `rgb(var(--tw-color-${primaryColor}))`,
  };
});
</script>

<style lang="scss" scoped>
.ds-alert {
  @apply tw-drop-shadow-sm tw-border tw-border-t-0 tw-border-gray-medium tw-rounded-md tw-text-xs tw-text-gray-dark;

  &__inner-wrapper {
    @apply tw-flex tw-items-center tw-bg-white tw-rounded-md tw-text-xs tw-px-3 tw-py-2 tw-border-t-5;
    border-color: var(--primary-color);
  }

  &__icon {
    @apply tw-mr-2 tw-self-start;
    color: var(--primary-color);
  }
}
</style>
